import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { zeosDialog } from '../../../components/Shared/confirm-dialog/ConfirmDialog';
import { ZeosApiContext } from '../../../context/ZeosApiContext';
import { CompanyForm } from '../../../forms/CompanyForm';
import { useFormSubmission } from '../../../hooks/useFormSubmission';
import { useFormValidation } from '../../../hooks/useFormValidation';
import {
    clearCompanyDraft, companyDraftSelector, setCompanyDraft
} from '../../../redux-store/OnboardingSlice';
import { IZeosUser } from '../../../services/auth/IZeosUser';
import { defaultCompanyForm } from '../../../services/forms/DefaultFormData';
import { ICompanyFormDataType } from '../../../services/forms/FormDataTypes';
import {
    CompanyValidationSchema
} from '../../../services/forms/validationSchemas/CompanyValidationSchema';
import { createLogger } from '../../../services/Logger';
import { ICompanyContract } from '../../../services/zeos-api/contracts/Company';
import { OnboardContainer } from '../layout/OnboardContainer';

const log = createLogger("CompanyScreen");

export const CompanyScreen = () => {
  const apiContext = useContext(ZeosApiContext);
  const dispatch = useDispatch();
  const companyDraft = useSelector(companyDraftSelector);

  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [originalFormData, setOriginalFormData] =
    useState<ICompanyFormDataType | null>(null);
  const [zeosUser, setZeosUser] = useState<IZeosUser>();

  // Send user data back to OnboardTemplate
  const handleUserCallback = (user: IZeosUser) => {
    setZeosUser(user);
  };

  // Seed from an in-progress draft (survives back/forward nav) if present;
  // the initAsync effect below overwrites this with real backend data once
  // it resolves, so a draft can never mask an already-saved company.
  const { formData, setFormData, errors, formValidate, formChange } =
    useFormValidation<ICompanyFormDataType>(
      CompanyValidationSchema,
      companyDraft ?? defaultCompanyForm
    );

  // Persist form input to the draft as the user types, so it survives
  // navigating away and back. Skipped while rehydrating from the backend to
  // avoid overwriting a good draft with the transient pre-load state.
  useEffect(() => {
    if (!isLoadingData) {
      dispatch(setCompanyDraft(formData));
    }
  }, [formData, isLoadingData, dispatch]);

  useEffect(() => {
    const initAsync = async () => {
      if (zeosUser) {
        setIsLoadingData(true);
        try {
          const existingCompanies = zeosUser.customerClaims[0]?.companies;
          const companyId = existingCompanies?.[0]?.companyId;

          if (!companyId) {
            if (!existingCompanies || existingCompanies.length === 0) {
              // New user with no prior companies — force the form blank in
              // case a draft from an earlier/abandoned registration in this
              // browser tab is still sitting in the onboarding Redux slice.
              log.warn("No existing companies for user, leaving form blank");
              setFormData(defaultCompanyForm);
              setOriginalFormData(defaultCompanyForm);
              dispatch(clearCompanyDraft());
              setIsFormDirty(false);
              return;
            }
            // Returning user whose claims may be stale — fall back to API lookup
            log.warn(
              "No company ID found in user data, trying to fetch by customer ID"
            );
            const customerId = zeosUser.customerClaims[0]?.customerId;
            if (customerId) {
              try {
                const companiesResponse =
                  await apiContext.Company.companiesByCustIdAsync(customerId);
                if (
                  companiesResponse.message !== "error" &&
                  companiesResponse.data &&
                  companiesResponse.data.length > 0
                ) {
                  const company = companiesResponse.data[0];
                  const formData: ICompanyFormDataType = {
                    companyName: company.companyName,
                    companyTypeId: company.companyTypeId ?? 0,
                    address: company.address,
                    suite: company.suite,
                    city: company.city,
                    state: company.state,
                    zip: company.zip,
                    website: company.website,
                    companyId: company.companyId,
                    customerId: company.customerId,
                  };
                  setFormData(formData);
                  setOriginalFormData(formData);
                  setIsFormDirty(false);
                  return;
                }
              } catch (error) {
                log.error("Failed to fetch company by customer ID", {
                  error,
                  customerId,
                });
              }
            }
            log.warn(
              "No company data available, user will need to fill out form again"
            );
            return;
          }

          const response = await apiContext.Company.getCompanyAsync(companyId);

          if (response.message === "error") {
            log.error("Failed to load company data", {
              error: response.message,
              companyId,
            });
            return;
          }

          const company = response.data;
          if (company) {
            const formData: ICompanyFormDataType = {
              companyName: company.companyName,
              companyTypeId: company.companyTypeId ?? 0,
              address: company.address,
              suite: company.suite,
              city: company.city,
              state: company.state,
              zip: company.zip,
              website: company.website,
              companyId: company.companyId,
              customerId: company.customerId,
              //Hold until needed
              // userCompanyRoles: formData.userCompanyRoles,
            };
            setFormData(formData);
            setOriginalFormData(formData);
            setIsFormDirty(false);
          }
        } catch (error) {
          log.error("Exception while loading company data", {
            error: error instanceof Error ? error.message : error,
            companyId: zeosUser.customerClaims[0].companies[0]?.companyId,
          });
        } finally {
          setIsLoadingData(false);
        }
      }
    };
    initAsync();
  }, [zeosUser?.zeosUserId]);

  // Check if form data has been modified by user
  const checkFormDirty = useCallback(() => {
    if (!originalFormData) return false;

    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  }, [formData, originalFormData]);

  // Update form dirty state when form data changes
  useEffect(() => {
    if (originalFormData) {
      const dirty = checkFormDirty();
      setIsFormDirty(dirty);
    }
  }, [formData, checkFormDirty, originalFormData]);

  const submitCompanyData = async (
    formData: ICompanyFormDataType,
    mode: "put" | "post"
  ) => {
    const customerId =
      mode === "put"
        ? formData.customerId
        : zeosUser?.customerClaims[0]?.customerId;

    if (!customerId && mode === "post") {
      throw new Error(
        "Customer ID not available - user may not be properly initialized"
      );
    }

    const payload: ICompanyContract = {
      companyName: formData.companyName,
      companyTypeId: formData.companyTypeId ?? 0,
      address: formData.address,
      suite: formData.suite,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
      website: formData.website,
      companyId: formData.companyId,
      customerId: customerId,
      //Hold until needed
      // userCompanyRoles: formData.userCompanyRoles,
    };

    const result =
      mode === "put"
        ? await apiContext.Company.putCompanyAsync(payload)
        : await apiContext.Company.postCompanyAsync(payload);

    if (result?.message === "error") {
      throw new Error("Company operation failed");
    }

    // Record the persisted ID so a repeat submit in this mount is a PUT, not
    // another POST (prevents duplicate creation on back/forward nav). The
    // create endpoint doesn't return the new company, so re-fetch it by
    // customer ID
    if (mode === "post" && customerId) {
      const companiesResponse =
        await apiContext.Company.companiesByCustIdAsync(customerId);
      const createdCompanyId = companiesResponse.data?.[0]?.companyId;
      if (createdCompanyId) {
        setFormData((prev) => ({ ...prev, companyId: createdCompanyId }));
        setOriginalFormData((prev) =>
          prev ? { ...prev, companyId: createdCompanyId } : prev
        );
      }
    }

    dispatch(clearCompanyDraft());
  };

  // Setup form submission handler — decide create vs. update by whether a
  // persisted company already exists, not by navigation direction.
  const { handleSubmission } = useFormSubmission<ICompanyFormDataType>(
    Number(formData.companyId) > 0,
    submitCompanyData
  );

  // Effectively the master controller for "Continue" button
  const handleNext = async (doNavigate: () => void) => {
    log.info("Company handleNext: Starting navigation", {
      isLoadingData,
      isFormDirty,
      formData,
    });

    // Skip validation and submission if data is loading and form is not dirty
    if (isLoadingData && !isFormDirty) {
      log.info(
        "Company handleNext: Skipping validation - data loading and not dirty"
      );
      doNavigate();
      return;
    }

    // Validate form data before submission
    log.info("Company handleNext: About to validate form", { formData });
    const isValid = await formValidate(async () => {
      // This success callback will be called if validation passes
      log.info("Company handleNext: Validation passed, submitting form");
      try {
        // Submit form data
        await handleSubmission(formData, async () => {
          // Navigate without progress tracking
          log.info(
            "Company handleNext: Form submission successful, navigating"
          );
          doNavigate();
        });
      } catch (error) {
        // Error is already logged in handleSubmission
        log.error("Company handleNext: Form submission failed", error);
        console.error("Failed to complete company onboarding step:", error);
        zeosDialog("Failed to save company information. Please try again.");
      }
    });

    // If validation failed, isValid will be false and we return early
    if (!isValid) {
      log.warn("Company handleNext: Validation failed, not navigating", {
        errors,
      });
      return;
    }
  };

  const form = (
    <CompanyForm
      formData={formData}
      errors={errors}
      handleChange={formChange}
      setFormData={setFormData}
    />
  );

  return (
    <OnboardContainer
      content={form}
      contentType="form"
      pageSlug="company"
      onNext={handleNext}
      userCallback={handleUserCallback}
    />
  );
};
